/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Cross2Icon,
  TrashIcon,
  Pencil1Icon,
  ArrowLeftIcon,
  ExternalLinkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@/shared/ui/radix-icons';
import { Anchor, Button, Image, Text, UnstyledButton } from '@/shared/ui';
import { formatLocalizedDateTime } from '@/shared/utils/date';
import { PublicationInlineEdit } from './PublicationInlineEdit';
import type { CatalogPublication } from '../types';
import { getPortalI18nKey, getPortalLabel } from '../types';
import { LeadAdBadge } from './LeadAdBadge';
import { isInstagramEditHidden } from '@/shared/utils/instagram-edit-policy';
import { canBoostPublication } from '@/shared/utils/portal-publish';
import type { MissingAdSetting } from '@/shared/utils/ad-readiness';
import { adSettingsHref } from '@features/property-management/components/ad-dialogs/AdSetupNotice';
import { BoostPanel } from '@features/property-management/components/publications-center/publication-row-header';
import {
  boostPublication,
  isKnownBoostErrorCode,
  resolveBoostErrorMessage,
} from '@features/property-management/components/publications-center/use-publications-actions';
import { useAdDialogs } from '@features/property-management/components/ad-dialogs/use-ad-dialogs';
import { boostErrorDetail, clampAdBudgetReais } from '@/shared/utils/ad-dialog-helpers';
import {
  API_BASE,
  buildHeaders,
} from '@features/property-management/components/publications-center/api';

const POST_URL_BUILDERS: Record<string, (id: string, permalink?: string) => string | null> = {
  instagram: (_id, permalink) => permalink ?? null,
  facebook_feed: (id) => {
    const parts = id.split('_');
    if (parts.length < 2) return null;
    return `https://www.facebook.com/${parts[0]}/posts/${parts.slice(1).join('_')}`;
  },
  facebook_marketplace: (id) => `https://www.facebook.com/marketplace/item/${id}`,
  // OLX reports the ad's real link once moderation accepts it; before that the
  // entry only has a placeholder id, which is not an ad to open.
  olx: (id, permalink) => permalink ?? (id && !id.startsWith('pending:') ? `https://www.olx.com.br/anuncio/${id}` : null),
};

/**
 * Builds the public post URL for a portal publication when a builder exists.
 *
 * @param portal - Portal identifier.
 * @param publicationId - The portal's publication/post identifier.
 * @param permalink - Optional pre-resolved permalink.
 * @returns The post URL, or null when the portal is unsupported.
 */
function getPostUrl(portal: string, publicationId: string, permalink?: string): string | null {
  const builder = POST_URL_BUILDERS[portal];
  return builder ? builder(publicationId, permalink) : null;
}

interface Props {
  entry: CatalogPublication;
  agencyId: string;
  onClose: () => void;
  onRemove: (entry: CatalogPublication) => Promise<void>;
  onSaved?: () => void;
  fbHasPaymentMethod?: boolean;
  /** Whether this entry's portal (Facebook/Instagram) is currently connected. */
  isConnected?: boolean;
  /** Agency's configured default daily budget (cents) for this entry's portal. */
  defaultBoostBudget?: number;
  /** Agency's configured default run duration (days) for this entry's portal. */
  defaultBoostDuration?: number;
  /** What the agency must still fill in before it can boost this entry's platform. */
  boostMissing?: MissingAdSetting[];
}

/**
 * Maps a portal identifier to the portal name used in the content API URL.
 *
 * @param portal - Portal identifier.
 * @returns The content API portal name, or null if unsupported.
 */
function contentApiPortal(portal: string): string | null {
  if (portal === 'facebook_feed' || portal === 'facebook_marketplace') return 'facebook';
  if (portal === 'instagram') return 'instagram';
  return null;
}

/**
 * Modal that shows a publication's full detail with edit, remove, and boost
 * actions for the selected portal entry.
 *
 * @param props - Entry, agency scope, and lifecycle callbacks.
 * @returns The publication detail modal element.
 */
export function PublicationDetailModal({
  entry, agencyId, onClose, onRemove, onSaved, fbHasPaymentMethod = false, isConnected = true,
  defaultBoostBudget = 0, defaultBoostDuration = 0, boostMissing,
}: Props) {
  const [view, setView] = useState<'detail' | 'edit'>('detail');
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [showBoostPanel, setShowBoostPanel] = useState(false);
  const [boosting, setBoosting] = useState(false);
  const { confirmSpend, showAdError, dialogs: adDialogs } = useAdDialogs();
  const [boostBudget, setBoostBudget] = useState(clampAdBudgetReais(Math.round(defaultBoostBudget / 100)));
  const [boostDuration, setBoostDuration] = useState(defaultBoostDuration);
  const { t } = useTranslation(['@features/properties', '@pages/layout']);

  const carouselBtnStyle: React.CSSProperties = {
    background: 'color-mix(in srgb, var(--text-primary) 45%, transparent)',
    color: 'var(--surface-primary)',
    border: 0,
  };

  /**
   * undefined = loading (skeleton)
   * null      = post has no images (hide banner)
   * string[]  = image URLs to display as carousel
   */
  const [bannerImages, setBannerImages] = useState<string[] | null | undefined>(() => {
    const apiPortal = contentApiPortal(entry.portal);
    return apiPortal && entry.publication_id
      ? undefined
      : entry.property_image
        ? [entry.property_image]
        : null;
  });
  const [carouselIndex, setCarouselIndex] = useState(0);

  useEffect(() => {
    const apiPortal = contentApiPortal(entry.portal);
    if (!apiPortal || !entry.publication_id) return;

    let cancelled = false;
    (async () => {
      try {
        const headers = await buildHeaders();
        const res = await axios.get(
          `${API_BASE}/portal-integration/agencies/${agencyId}/publications/${apiPortal}/content`,
          { params: { post_id: entry.publication_id }, headers },
        );
        if (cancelled) return;
        const images: unknown = res.data?.images;
        if (Array.isArray(images) && images.length > 0) {
          setBannerImages(images as string[]);
        } else if (Array.isArray(images)) {
          setBannerImages(null);
        } else {
          setBannerImages(entry.property_image ? [entry.property_image] : null);
        }
      } catch {
        if (!cancelled) {
          setBannerImages(entry.property_image ? [entry.property_image] : null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleRemove = async () => {
    setRemoving(true);
    try {
      await onRemove(entry);
      onClose();
    } catch {
      toast.error(t('publications.toasts.removeError'));
    } finally {
      setRemoving(false);
    }
  };

  const boostPortal = contentApiPortal(entry.portal) === 'instagram' ? 'instagram' : 'facebook';

  const handleBoost = async () => {
    if (!entry.publication_id) return;
    const accepted = await confirmSpend({
      kind: 'boost',
      items: [{ label: 'boost', dailyBudgetCents: boostBudget * 100, durationDays: boostDuration }],
    });
    if (!accepted) return;
    setBoosting(true);
    try {
      await boostPublication(agencyId, entry.publication_id, boostBudget * 100, boostDuration, boostPortal);
      setShowBoostPanel(false);
      toast.success(t('publications.toasts.boostSuccess'));
      onSaved?.();
      onClose();
    } catch (error) {
      if (isKnownBoostErrorCode(error)) toast.error(resolveBoostErrorMessage(error, t));
      else showAdError({ kind: 'boost', detail: boostErrorDetail(error) });
    } finally {
      setBoosting(false);
    }
  };

  const portalLabel = t(getPortalI18nKey(entry.portal), { defaultValue: getPortalLabel(entry.portal) });
  const dateLabel = entry.published_at
    ? formatLocalizedDateTime(entry.published_at)
    : null;
  const postUrl = entry.publication_id
    ? getPostUrl(entry.portal, entry.publication_id, entry.permalink)
    : null;
  const canBoost = contentApiPortal(entry.portal) !== null && canBoostPublication({
    portal: boostPortal,
    postId: entry.publication_id,
    adId: entry.ad_id,
    isConnected,
    fbHasPaymentMethod,
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="publication-detail-modal"
      className="fixed inset-0 z-[1200] flex items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-[var(--surface-inverse)]/50" />

      <div
        className="relative w-full max-w-sm mx-(--space-4) rounded-(--radius-xl) overflow-hidden shadow-2xl flex flex-col max-h-[90vh]"
        style={{ background: 'var(--surface-primary)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── DETAIL VIEW ─────────────────────────────────────── */}
        {view === 'detail' && (
          <>
            {/* Banner: skeleton while loading, nothing if post has no images, carousel otherwise */}
            {bannerImages === undefined && (
              <div
                className="w-full animate-pulse"
                style={{ aspectRatio: '16 / 7', background: 'var(--surface-tertiary)' }}
                data-testid="detail-banner-skeleton"
              />
            )}
            {bannerImages && bannerImages.length > 0 && (
              <div className="relative w-full overflow-hidden" style={{ aspectRatio: '16 / 7' }}>
                <Image
                  src={bannerImages[carouselIndex]}
                  alt={entry.property_title ?? ''}
                  className="w-full h-full object-cover"
                  data-testid="detail-banner-image"
                />
                <div
                  className="absolute inset-0"
                  style={{
                    background: 'linear-gradient(to bottom, transparent 40%, color-mix(in srgb, var(--text-primary) 70%, transparent) 100%)',
                  }}
                />

                {/* Prev arrow */}
                {bannerImages.length > 1 && (
                  <UnstyledButton
                    type="button"
                    onClick={() => setCarouselIndex((i) => i - 1)}
                    disabled={carouselIndex === 0}
                    className="absolute left-(--space-2) top-(--space-1)/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center cursor-pointer disabled:opacity-20 transition-opacity hover:opacity-80"
                    style={carouselBtnStyle}
                    aria-label={t('publications.catalog.photoPrev')}
                    data-testid="detail-carousel-prev"
                  >
                    <ChevronLeftIcon width={14} height={14} />
                  </UnstyledButton>
                )}

                {/* Next arrow */}
                {bannerImages.length > 1 && (
                  <UnstyledButton
                    type="button"
                    onClick={() => setCarouselIndex((i) => i + 1)}
                    disabled={carouselIndex === bannerImages.length - 1}
                    className="absolute right-(--space-2) top-(--space-1)/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center cursor-pointer disabled:opacity-20 transition-opacity hover:opacity-80"
                    style={carouselBtnStyle}
                    aria-label={t('publications.catalog.photoNext')}
                    data-testid="detail-carousel-next"
                  >
                    <ChevronRightIcon width={14} height={14} />
                  </UnstyledButton>
                )}

                {/* Counter */}
                {bannerImages.length > 1 && (
                  <Text component="span"
                    className="absolute bottom-(--space-2) left-(--space-1)/2 -translate-x-1/2 px-(--space-2) py-(--space-0).5 rounded-full text-xs font-semibold bg-[var(--surface-inverse)]/45 text-[var(--text-inverse)]"
                    data-testid="detail-carousel-counter"
                  >
                    {carouselIndex + 1} / {bannerImages.length}
                  </Text>
                )}
              </div>
            )}

            <UnstyledButton
              type="button"
              data-testid="detail-modal-close"
              onClick={onClose}
              className="absolute top-(--space-3) right-(--space-3) w-8 h-8 rounded-full flex items-center justify-center cursor-pointer border-none z-10 bg-[var(--surface-inverse)]/40 text-[var(--text-inverse)]"
              aria-label={t('menu.close', { ns: '@pages/layout' })}
            >
              <Cross2Icon width={14} height={14} />
            </UnstyledButton>

            <div className="px-(--space-4) py-(--space-4) flex flex-col gap-(--space-3)">
              <div>
                <Text as="p" className="m-(--space-0) font-bold text-sm" style={{ color: 'var(--text-primary)' }}>
                  {entry.property_title ?? t('publications.catalog.noTitle')}
                </Text>
                <Text as="p" className="m-(--space-0) text-xs mt-(--space-1)" style={{ color: 'var(--text-secondary)' }}>
                  {portalLabel}
                </Text>
                {dateLabel && (
                  <Text as="p" className="m-(--space-0) text-xs mt-(--space-0).5" style={{ color: 'var(--text-tertiary)' }}>
                    {dateLabel}
                  </Text>
                )}
                <div className="mt-(--space-1-5) flex flex-wrap gap-(--space-1)">
                  <LeadAdBadge entry={entry} />
                </div>
                {entry.publication_id && (
                  <Text
                    data-testid="detail-publication-id"
                    as="p"
                    className="m-(--space-0) text-xs mt-(--space-1) truncate"
                    style={{ color: 'var(--text-tertiary)' }}
                  >
                    {t('publications.catalog.publicationId', { id: entry.publication_id })}
                  </Text>
                )}
              </div>

              <div className="flex flex-col gap-(--space-2) pt-(--space-1)">
                {postUrl && (
                  <Anchor
                    href={postUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="detail-view-btn"
                    className="w-full py-(--space-2) text-xs font-semibold rounded-(--radius-lg) border flex items-center justify-center gap-(--space-1).5 no-underline hover:opacity-80 transition-opacity"
                    style={{
                      background: 'transparent',
                      color: 'var(--text-primary)',
                      borderColor: 'var(--border-subtle)',
                    }}
                  >
                    <ExternalLinkIcon width={12} height={12} />
                    {t('publications.viewPost')}
                  </Anchor>
                )}

                {canBoost && !showBoostPanel && (
                  <Button
                    type="button"
                    data-testid="detail-boost-btn"
                    onClick={() => setShowBoostPanel(true)}
                    className="w-full py-(--space-2) text-xs font-bold rounded-(--radius-lg) cursor-pointer hover:opacity-90 transition-opacity"
                    style={{ background: 'transparent', color: 'var(--status-warning-text)', borderColor: 'var(--status-warning-text)', border: '1px solid' }}
                  >
                    {t('publications.actions.boost')}
                  </Button>
                )}

                {canBoost && showBoostPanel && (
                  <BoostPanel
                    rowKey="detail"
                    portal={boostPortal}
                    t={t}
                    budget={boostBudget}
                    duration={boostDuration}
                    isBoosting={boosting}
                    onBudget={setBoostBudget}
                    onDuration={setBoostDuration}
                    missing={boostMissing}
                    settingsHref={adSettingsHref(agencyId)}
                    onCancel={() => setShowBoostPanel(false)}
                    onConfirm={handleBoost}
                  />
                )}

                {!isInstagramEditHidden(entry.portal) && (
                  <Button
                    type="button"
                    data-testid="detail-publish-btn"
                    onClick={() => setView('edit')}
                    className="w-full py-(--space-2) text-xs font-bold rounded-(--radius-lg) cursor-pointer hover:opacity-90 transition-opacity"
                    style={{ background: 'var(--seed-base)', color: 'var(--surface-primary)' }}
                  >
                    <Pencil1Icon width={12} height={12} className="inline mr-(--space-1).5" />
                    {t('publications.editPublication')}
                  </Button>
                )}

                {!confirmRemove ? (
                  <Button
                    type="button"
                    data-testid="detail-remove-btn"
                    onClick={() => setConfirmRemove(true)}
                    className="w-full py-(--space-2) text-xs font-semibold rounded-(--radius-lg) border cursor-pointer hover:opacity-80 transition-opacity"
                    style={{
                      background: 'transparent',
                      color: 'var(--status-error-text)',
                      borderColor: 'var(--status-error-text)',
                    }}
                  >
                    <TrashIcon width={12} height={12} className="inline mr-(--space-1).5" />
                    {t('publications.catalog.removePublication')}
                  </Button>
                ) : (
                  <div className="flex gap-(--space-2)">
                    <Button
                      type="button"
                      data-testid="detail-cancel-remove-btn"
                      onClick={() => setConfirmRemove(false)}
                      className="flex-1 py-(--space-2) text-xs font-semibold rounded-(--radius-lg) border cursor-pointer hover:opacity-80"
                      style={{
                        background: 'transparent',
                        color: 'var(--text-secondary)',
                        borderColor: 'var(--border-subtle)',
                      }}
                    >
                      {t('publications.actions.cancel')}
                    </Button>
                    <Button
                      type="button"
                      data-testid="detail-confirm-remove-btn"
                      disabled={removing}
                      onClick={handleRemove}
                      className="flex-1 py-(--space-2) text-xs font-bold rounded-(--radius-lg) cursor-pointer disabled:opacity-50 hover:opacity-90"
                      style={{ background: 'var(--status-error-text)', color: 'var(--surface-primary)' }}
                    >
                      {removing ? '...' : t('publications.actions.confirm')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* ── EDIT VIEW ───────────────────────────────────────── */}
        {view === 'edit' && (
          <div className="flex flex-col overflow-hidden">
            <div
              className="flex items-center gap-(--space-2) px-(--space-4) py-(--space-3) border-b shrink-0"
              style={{ borderColor: 'var(--border-subtle)' }}
            >
              <UnstyledButton
                type="button"
                onClick={() => setView('detail')}
                className="flex items-center gap-(--space-1).5 text-xs font-semibold cursor-pointer bg-transparent border-none p-(--space-0) hover:opacity-70 transition-opacity"
                style={{ color: 'var(--text-secondary)' }}
                data-testid="detail-back-btn"
              >
                <ArrowLeftIcon width={13} height={13} />
                {t('publications.actions.cancel')}
              </UnstyledButton>
              <Text
                as="span"
                className="text-xs font-bold flex-1 text-center pr-(--space-16)"
                style={{ color: 'var(--text-primary)' }}
              >
                {entry.property_title ?? portalLabel}
              </Text>
            </div>

            <div className="overflow-y-auto p-(--space-4)">
              <PublicationInlineEdit
                entry={entry}
                agencyId={agencyId}
                onDone={() => setView('detail')}
                onSaved={() => {
                  onSaved?.();
                  onClose();
                }}
              />
            </div>
          </div>
        )}
      </div>
      {adDialogs}
    </div>
  );
}
